/**
 * composerBus — window-event channel for putting text INTO the agent composer
 * without sending it.
 *
 * The composer's text is component-local state inside <ChatComposer>, which
 * mounts with the agent panel — meaning it is NOT always alive when a surface
 * dispatches, and worse, the instance alive AT dispatch may not be the one
 * the reader ends up looking at: the first-run rail's ask beat prefills and
 * then opens the panel/conversation, which REMOUNTS the composer, so text
 * applied to the old instance dies with it. (Filmed twice: the event-only
 * channel dropped the text; an event-plus-buffer where the live handler also
 * claimed the buffer dropped it again, because the claiming instance was the
 * one about to die.)
 *
 * So the channel is event plus a SHORT-LIVED buffer that only mounting
 * composers claim:
 *   - dispatch parks the text and fires the event;
 *   - a live composer applies from the event but leaves the buffer alone;
 *   - a composer that mounts within the TTL claims the buffer (and clears
 *     it), which is exactly the remount-right-after-dispatch case;
 *   - the TTL bounds the one cost of not claiming on apply: a composer
 *     mounting elsewhere moments after a prefill re-applies it. Five seconds
 *     keeps that to navigations that immediately follow the dispatch — which
 *     are the intended case, not a hazard.
 *
 * Same idiom as `canvas/commentBus.ts` and the existing `chai:artifact-export`
 * channel — dispatch here, listen in the composer.
 *
 * Note this is PREFILL, not send: the workspace's suggestion chips
 * (`ChatEmpty`) submit immediately, which is a different affordance.
 */

export const PREFILL_COMPOSER = "chai:prefill-composer";

export interface PrefillComposerDetail {
  text: string;
}

const PENDING_TTL_MS = 5_000;

let pending: { text: string; at: number } | null = null;

/** The most recent prefill this page ever dispatched — NO TTL, overwritten by
 *  the next prefill. Kept separately from `pending` (whose TTL bounds the
 *  mount-time re-apply hazard) so the send path can tell a suggested prompt
 *  from a typed question long after the five seconds are up: a prospect who
 *  reads the prefilled prompt for a minute and then presses send is still
 *  sending the SUGGESTION, not a question of their own. */
let lastPrefill: string | null = null;

export function prefillComposer(text: string): void {
  pending = { text, at: Date.now() };
  lastPrefill = text;
  window.dispatchEvent(
    new CustomEvent<PrefillComposerDetail>(PREFILL_COMPOSER, { detail: { text } }),
  );
}

/** Was `text` (exactly) put into the composer by a prefill? Used by the send
 *  path to route an UNEDITED suggested prompt through the recorded-ask replay
 *  the way a chip click would — one edited character and it goes live. */
export function isPrefilledText(text: string): boolean {
  return lastPrefill !== null && text === lastPrefill;
}

/** Ask the live composer to submit whatever it currently holds — the demo
 *  rail's own Send button uses this so "send" means the SAME send the
 *  composer's button performs (same validation, same clear, same routing
 *  through the recorded-replay path for an unedited prefill). A no-op when
 *  no composer is mounted or it holds only whitespace — and
 *  `requestComposerSubmit` SAYS so, because a Send button that did nothing
 *  was exactly what a narrow window showed (no composer was mounted). */
export const SUBMIT_COMPOSER = "chai:submit-composer";

export interface SubmitComposerDetail {
  /** Set by the composer that submitted. */
  handled: boolean;
}

/** Announced by the composer AFTER a successful submit, carrying the sent
 *  text — the demo rail listens so its ask beat can gate "keep going" on the
 *  question actually having been sent, whichever button sent it. */
export const COMPOSER_SENT = "chai:composer-sent";

export interface ComposerSentDetail {
  text: string;
}

/** `true` when a mounted composer took the submit. */
export function requestComposerSubmit(): boolean {
  const detail: SubmitComposerDetail = { handled: false };
  window.dispatchEvent(new CustomEvent<SubmitComposerDetail>(SUBMIT_COMPOSER, { detail }));
  return detail.handled;
}

// ── open the agent panel, and send without a visible composer ───────────────
//
// The rail's ask beat staged its question in "the composer on your right" and
// then pressed Send on it. Below ~768px (and with the panel closed, overlaid,
// or showing its Comments tab) there IS no composer to see: the rail promised
// a lit box that was not there, and its Send did nothing (Oxera, filmed). So a
// surface can also (1) ask the app shell to OPEN the agent panel, at any
// width, and (2) SEND through whichever composer is — or is about to be —
// mounted on the page, without ever showing the box.

/** Open the agent panel: the docked rail, the narrow-window sheet or the
 *  phone sheet, on its Agent tab. Heard by the app shell (routes/_app.tsx)
 *  and the tabbed rail (UnifiedRail). */
export const OPEN_AGENT_PANEL = "chai:open-agent-panel";

export function requestAgentPanel(): void {
  window.dispatchEvent(new CustomEvent(OPEN_AGENT_PANEL));
}

export function onAgentPanelRequest(handler: () => void): () => void {
  window.addEventListener(OPEN_AGENT_PANEL, handler);
  return () => window.removeEventListener(OPEN_AGENT_PANEL, handler);
}

/** A send was parked (`sendThroughComposer`) — live composers try to claim it. */
export const SEND_PARKED = "chai:send-parked";

/** How long a composer must have stayed mounted before it may claim a parked
 *  send. The panel opening (or a navigation) can mount and drop a composer
 *  in quick succession; only an instance that survives this long sends, and
 *  one that unmounts sooner never touched the send, so nothing is lost. */
export const SEND_SETTLE_MS = 300;

/** A parked send is stale after this — long enough for a panel to open and a
 *  page's thread to load, short enough that nobody is surprised by it. */
export const SEND_TTL_MS = 8_000;

let pendingSend: { text: string; path: string; at: number } | null = null;

/**
 * Send `text` through the agent composer without needing to see it.
 *
 * Parks the send and tells live composers; a composer claims it after
 * staying mounted SEND_SETTLE_MS — whether it was already mounted or mounts
 * because the panel just opened. Exactly ONE composer sends it (the claim
 * takes it). `path` pins the send to a page: a composer elsewhere (a
 * navigation still in flight) leaves it alone.
 *
 * The text counts as a prefill (`isPrefilledText`), so the panel routes an
 * unedited suggested prompt through its recorded replay exactly as if the
 * composer's own button had sent it. The composer announces the send
 * (`onComposerSent`) like any other.
 */
export function sendThroughComposer(text: string, path = ""): void {
  lastPrefill = text;
  pendingSend = { text, path, at: Date.now() };
  window.dispatchEvent(new CustomEvent(SEND_PARKED));
}

/** The canvas (and artifact, if any) a path shows — its `/c/<id>` and
 *  `/a/<id>` segments, without the workspace slug. "" off a canvas. */
function pageOf(path: string): string {
  const canvas = /\/c\/([^/?#]+)/.exec(path)?.[1] ?? "";
  if (!canvas) return "";
  return `${canvas}/${/\/a\/([^/?#]+)/.exec(path)?.[1] ?? ""}`;
}

/** Is `pathname` the page a send was pinned to? Canvas pages compare by
 *  canvas (and artifact) id, so a workspace slug the route canonicalised on
 *  arrival — a demo's authored href can carry a stale one — still matches. */
function samePage(path: string, pathname: string): boolean {
  if (!path || path === pathname) return true;
  const page = pageOf(path);
  return page !== "" && page === pageOf(pathname);
}

/** Take the parked send if it is fresh and for `pathname`; `null` otherwise.
 *  A CLAIM, unlike `consumePendingPrefill`: a prefill applied twice is
 *  harmless, a question sent twice is two answers. */
export function claimPendingSend(pathname: string): string | null {
  if (!pendingSend) return null;
  if (Date.now() - pendingSend.at > SEND_TTL_MS) {
    pendingSend = null;
    return null;
  }
  if (!samePage(pendingSend.path, pathname)) return null;
  const { text } = pendingSend;
  pendingSend = null;
  return text;
}

export function announceComposerSent(text: string): void {
  window.dispatchEvent(new CustomEvent<ComposerSentDetail>(COMPOSER_SENT, { detail: { text } }));
}

export function onComposerSent(handler: (text: string) => void): () => void {
  const listener = (e: Event) => {
    const text = (e as CustomEvent<ComposerSentDetail>).detail?.text ?? "";
    handler(text);
  };
  window.addEventListener(COMPOSER_SENT, listener);
  return () => window.removeEventListener(COMPOSER_SENT, listener);
}

/** Read a recent prefill. For MOUNTING composers only — the live event
 *  handler must not call this (its instance may be the one a navigation is
 *  about to destroy).
 *
 *  Deliberately a PEEK, not a claim: only the TTL retires the buffer.
 *  Claim-on-first-mount was tried and filmed failing — the panel can
 *  remount MORE than once in quick succession, the first short-lived mount
 *  claimed the text and died, and the surviving composer found an empty
 *  buffer. With a peek, every mount inside the window applies; the last
 *  one standing keeps the text, and a dead one's copy dies with it. The
 *  cost is bounded by the TTL: a composer mounting elsewhere within five
 *  seconds of a prefill re-applies it, which is the intended handoff, not
 *  a hazard (the app renders one composer at a time). */
export function consumePendingPrefill(): string | null {
  if (pending && Date.now() - pending.at <= PENDING_TTL_MS) return pending.text;
  pending = null;
  return null;
}
